import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SwUpdate } from '@angular/service-worker';
import Swal from 'sweetalert2';

type InstallPlatform = 'ios' | 'android' | 'mac-safari' | 'firefox-desktop' | 'desktop';

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  public isInstallable = new BehaviorSubject<boolean>(false);
  public updateAvailable = new BehaviorSubject<boolean>(false);

  constructor(private swUpdate: SwUpdate) {
    this.initPWA();
  }

  private initPWA(): void {
    // index.html captures beforeinstallprompt before Angular boots and re-announces it as pwa-prompt-ready.
    if (this.getPromptEvent()) {
      this.isInstallable.next(true);
    }
    window.addEventListener('pwa-prompt-ready', () => this.isInstallable.next(true));

    window.addEventListener('appinstalled', () => {
      (window as any).deferredPwaPrompt = null;
      localStorage.setItem('pwa_installed', 'true');
      this.isInstallable.next(false);
      void Swal.fire({
        title: 'Link is installed',
        text: 'Open Link from your home screen, Start menu, Dock or Launchpad.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    });

    // Check for updates
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(event => {
        if (event.type === 'VERSION_READY') {
          this.updateAvailable.next(true);
        }
      });
    }
  }

  public isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true ||
           document.referrer.includes('android-app://');
  }

  public getPromptEvent(): any {
    return (window as any).deferredPwaPrompt || null;
  }

  public isIos(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iPadOS 13+ reports itself as a Mac with touch support.
    return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  }

  private getPlatform(): InstallPlatform {
    const ua = navigator.userAgent || '';
    if (this.isIos()) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    if (/Firefox\//.test(ua)) return 'firefox-desktop';
    if (/Macintosh/.test(ua) && /Safari\//.test(ua) && !/Chrome|Chromium|Edg\/|OPR\//.test(ua)) return 'mac-safari';
    return 'desktop';
  }

  /**
   * Every Install button lands here. It always shows the installation guide first; where the browser
   * supports it (Chrome, Edge, Samsung Internet) the guide's Install now button opens the native dialog.
   * Safari (iPhone, iPad, Mac) and Firefox have no install API, so the guide's steps are the only way.
   */
  public async installPWA(): Promise<void> {
    if (this.isStandalone()) {
      void Swal.fire({
        title: 'Link is installed',
        text: 'You are already using the installed Link app.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
      return;
    }

    const guideOptions = (hasPrompt: boolean) => {
      const { title, steps, note } = this.getGuide(hasPrompt ? 'native' : this.getPlatform());
      return {
        title,
        html:
          '<div class="pwa-guide">' +
          '<p class="pwa-guide-intro">Install Link to open it from your home screen, dock or desktop like any other app, ' +
          'in its own window, with notifications.</p>' +
          '<ol>' + steps.map((s) => `<li>${s}</li>`).join('') + '</ol>' +
          (note ? `<p class="pwa-guide-note">${note}</p>` : '') +
          '</div>',
        showCancelButton: hasPrompt,
        confirmButtonText: hasPrompt ? 'Install now' : 'Got it',
      };
    };

    // Chrome and Edge can offer the install a little after the page loads; turn the open guide into Install now then.
    const onPromptReady = () => Swal.update(guideOptions(true));
    window.addEventListener('pwa-prompt-ready', onPromptReady);
    let result;
    try {
      result = await Swal.fire({
        ...guideOptions(!!this.getPromptEvent()),
        icon: 'info',
        cancelButtonText: 'Not now',
        reverseButtons: true,
      });
    } finally {
      window.removeEventListener('pwa-prompt-ready', onPromptReady);
    }

    const prompt = this.getPromptEvent();
    if (!prompt || !result.isConfirmed) return;

    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice?.outcome === 'accepted') {
        localStorage.setItem('pwa_installed', 'true');
        this.isInstallable.next(false);
      }
    } catch (err) {
      console.error('PWA install prompt failed:', err);
    } finally {
      // A prompt event can only be used once; the browser fires a new one if Link is still installable.
      (window as any).deferredPwaPrompt = null;
    }
  }

  private getGuide(platform: InstallPlatform | 'native'): { title: string; steps: string[]; note?: string } {
    switch (platform) {
      case 'native':
        return {
          title: 'Install Link',
          steps: [
            'Click <b>Install now</b> below.',
            'Your browser asks you to confirm. Choose <b>Install</b>.',
            'Link opens in its own window. Find it later on your home screen or app drawer (phone), or in the Start menu, ' +
              'desktop, Dock or Launchpad (computer).',
          ],
        };
      case 'ios':
        return {
          title: 'Install Link on iPhone or iPad',
          steps: [
            'Tap the <b>Share</b> button (a square with an arrow pointing up). In Safari it is in the toolbar; ' +
              'if you do not see it, tap <b>•••</b> first.',
            'Scroll down and tap <b>Add to Home Screen</b>.',
            'Keep <b>Open as Web App</b> switched on, then tap <b>Add</b>.',
            'Open Link from the new icon on your Home Screen and sign in.',
          ],
          note: 'On iPhone and iPad, notifications only work in the app opened from the Home Screen.',
        };
      case 'android':
        return {
          title: 'Install Link on Android',
          steps: [
            'Tap the browser menu: <b>⋮</b> at the top right in Chrome, or <b>≡</b> at the bottom in Samsung Internet.',
            'Tap <b>Install app</b> or <b>Add to Home screen</b> (Samsung Internet: <b>Add page to → Home screen</b>).',
            'Tap <b>Install</b> to confirm.',
            'Open Link from your home screen or app drawer.',
          ],
        };
      case 'mac-safari':
        return {
          title: 'Install Link on Mac (Safari)',
          steps: [
            'In the menu bar, choose <b>File → Add to Dock</b> (or click <b>Share</b> in the toolbar, then <b>Add to Dock</b>).',
            'Click <b>Add</b>.',
            'Open Link from the Dock or Launchpad.',
          ],
          note: 'Needs macOS Sonoma or later. On older macOS, open Link in Chrome or Edge to install it.',
        };
      case 'firefox-desktop':
        return {
          title: 'Install Link',
          steps: [
            'Firefox on a computer cannot install web apps.',
            'Open Link in <b>Chrome</b> or <b>Edge</b> (or <b>Safari</b> on a Mac) and choose <b>Install App</b> again.',
          ],
        };
      default:
        return {
          title: 'Install Link on your computer',
          steps: [
            'Click the <b>Install</b> icon (a screen with a down arrow) at the right end of the address bar.',
            'No icon? Chrome: <b>⋮ → Cast, save, and share → Install page as app</b>. ' +
              'Edge: <b>⋯ → Apps → Install this site as an app</b>.',
            'Click <b>Install</b> to confirm.',
            'Open Link from the Start menu, desktop, Dock or Launchpad.',
          ],
          note: 'If you closed the install popup before, the browser keeps the option in these menus.',
        };
    }
  }

  public async checkForUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        console.log('Check for update completed:', updateFound);
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    }
  }

  public async activateUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      try {
        await this.swUpdate.activateUpdate();
        document.location.reload();
      } catch (err) {
        console.error('Failed to activate update:', err);
      }
    }
  }
}
